import React, { useState } from "react";
import {
  Box, Typography, Grid, Paper, Switch, FormControlLabel, FormControl,
  InputLabel, Select, MenuItem, Slider, TextField, Chip, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, Divider, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TuneIcon from "@mui/icons-material/Tune";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import SpeedIcon from "@mui/icons-material/Speed";
import BoltIcon from "@mui/icons-material/Bolt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InsightsIcon from "@mui/icons-material/Insights";
import StraightenIcon from "@mui/icons-material/Straighten";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import CampaignIcon from "@mui/icons-material/Campaign";
import SyncIcon from "@mui/icons-material/Sync";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CasinoIcon from "@mui/icons-material/Casino";
import CloudIcon from "@mui/icons-material/Cloud";

const INDICATOR_TYPES = [
  { id: "FAST_GREEN_SCALP_5X", label: "Fast Green Scalp 5×", icon: <BoltIcon sx={{ fontSize: 16 }} />, color: "#00FF94", category: "Scalp" },
  { id: "EMA_CROSS", label: "EMA Cross", icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, color: "#3b82f6", category: "Trend" },
  { id: "RSI", label: "RSI", icon: <BarChartIcon sx={{ fontSize: 16 }} />, color: "#F97316", category: "Oscillator" },
  { id: "MACD", label: "MACD", icon: <TrendingDownIcon sx={{ fontSize: 16 }} />, color: "#A855F7", category: "Momentum" },
  { id: "VWAP", label: "VWAP", icon: <InsightsIcon sx={{ fontSize: 16 }} />, color: "#3b82f6", category: "Volume" },
  { id: "ATR_FILTER", label: "ATR Filter", icon: <StraightenIcon sx={{ fontSize: 16 }} />, color: "#F97316", category: "Volatility" },
  { id: "SUPPORT_RESISTANCE", label: "Support / Resistance", icon: <GpsFixedIcon sx={{ fontSize: 16 }} />, color: "#EF4444", category: "Structure" },
  { id: "VOLUME_SPIKE", label: "Volume Spike", icon: <CampaignIcon sx={{ fontSize: 16 }} />, color: "#F97316", category: "Volume" },
  { id: "TREND_REGIME", label: "Trend Regime", icon: <SyncIcon sx={{ fontSize: 16 }} />, color: "#6B7280", category: "Trend" },
  { id: "BOLLINGER_BANDS", label: "Bollinger Bands", icon: <SquareFootIcon sx={{ fontSize: 16 }} />, color: "#3b82f6", category: "Volatility" },
  { id: "SUPERTREND", label: "SuperTrend", icon: <RocketLaunchIcon sx={{ fontSize: 16 }} />, color: "#EF4444", category: "Trend" },
  { id: "STOCHASTIC", label: "Stochastic Oscillator", icon: <CasinoIcon sx={{ fontSize: 16 }} />, color: "#A855F7", category: "Oscillator" },
  { id: "ICHIMOKU", label: "Ichimoku Cloud", icon: <CloudIcon sx={{ fontSize: 16 }} />, color: "#3b82f6", category: "Trend" },
];

const TIMEFRAME_OPTIONS = ["1m", "5m", "15m", "30m", "1h", "4h"];

const INDICATOR_DEFAULTS = {
  EMA_CROSS: { fastPeriod: 9, slowPeriod: 21 },
  RSI: { period: 14, overbought: 70, oversold: 30 },
  MACD: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
  VWAP: { anchorPeriod: "session" },
  ATR_FILTER: { period: 14, minAtrPct: 0.1, maxAtrPct: 3.0 },
  SUPPORT_RESISTANCE: { lookbackBars: 50, zonePctWidth: 0.3 },
  VOLUME_SPIKE: { lookbackBars: 20, spikeMultiplier: 2.0 },
  TREND_REGIME: { adxPeriod: 14, adxTrendThreshold: 25, emaPeriod: 50 },
  FAST_GREEN_SCALP_5X: { requireTrendConfirm: true, fastGreenTimeoutSec: 90 },
  BOLLINGER_BANDS: { period: 20, stdDev: 2 },
  SUPERTREND: { atrPeriod: 10, multiplier: 3.0 },
  STOCHASTIC: { kPeriod: 14, dPeriod: 3, smoothK: 3, overbought: 80, oversold: 20 },
  ICHIMOKU: { tenkanPeriod: 9, kijunPeriod: 26, senkouBPeriod: 52 },
};

const DEFAULT_WEIGHTS = {
  FAST_GREEN_SCALP_5X: 0.50, EMA_CROSS: 0.40, RSI: 0.35, MACD: 0.35,
  VWAP: 0.30, ATR_FILTER: 0.25, SUPPORT_RESISTANCE: 0.35,
  VOLUME_SPIKE: 0.40, TREND_REGIME: 0.45,
  BOLLINGER_BANDS: 0.35, SUPERTREND: 0.45, STOCHASTIC: 0.35, ICHIMOKU: 0.40,
};

const DEFAULT_TF = {
  EMA_CROSS: ["5m"], RSI: ["5m"], MACD: ["5m"], VWAP: ["5m"],
  ATR_FILTER: ["5m"], SUPPORT_RESISTANCE: ["15m"], VOLUME_SPIKE: ["5m"],
  TREND_REGIME: ["15m"], FAST_GREEN_SCALP_5X: ["1m", "5m"],
  BOLLINGER_BANDS: ["5m"], SUPERTREND: ["5m"], STOCHASTIC: ["5m"], ICHIMOKU: ["15m"],
};

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

function HealthBadge({ indicators }) {
  const enabled = indicators.filter(i => i.enabled);
  if (enabled.length === 0) return <Chip size="small" label="No indicators" color="default" />;
  const avgWeight = enabled.reduce((s, i) => s + (i.weight || 0.1), 0) / enabled.length;
  if (avgWeight > 0.12 && enabled.length >= 3) return <Chip size="small" label="HIGH" color="success" sx={{ fontWeight: "bold" }} />;
  if (enabled.length >= 2) return <Chip size="small" label="MED" color="warning" sx={{ fontWeight: "bold" }} />;
  return <Chip size="small" label="LOW" color="error" sx={{ fontWeight: "bold" }} />;
}

function IndicatorCard({ indicator, onUpdate, onDelete, onDuplicate }) {
  const typeInfo = INDICATOR_TYPES.find(t => t.id === indicator.type) || { label: indicator.type, icon: "?", color: "#999" };
  const [expanded, setExpanded] = useState(false);

  const handleParamChange = (key, value) => {
    onUpdate({ ...indicator, params: { ...indicator.params, [key]: value } });
  };

  const handleTfToggle = (tf) => {
    const tfs = indicator.timeframes || [];
    const newTfs = tfs.includes(tf) ? tfs.filter(t => t !== tf) : [...tfs, tf];
    if (newTfs.length > 0) onUpdate({ ...indicator, timeframes: newTfs });
  };

  return (
    <Paper
      elevation={indicator.enabled ? 2 : 0}
      sx={{
        p: 1.5, mb: 1, borderRadius: 2,
        border: "1px solid",
        borderColor: indicator.enabled ? typeInfo.color : "divider",
        opacity: indicator.enabled ? 1 : 0.6,
        transition: "all 0.2s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch
            size="small"
            checked={indicator.enabled}
            onChange={(e) => onUpdate({ ...indicator, enabled: e.target.checked })}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: typeInfo.color } }}
          />
          <Typography variant="body2" fontWeight="bold" sx={{ color: indicator.enabled ? typeInfo.color : "text.secondary" }}>
            {typeInfo.icon} {typeInfo.label}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {(indicator.timeframes || []).map(tf => (
            <Chip key={tf} size="small" label={tf} variant="outlined"
              sx={{ height: 20, fontSize: "0.65rem", borderColor: typeInfo.color, color: typeInfo.color }} />
          ))}
          <Tooltip title="Duplicate indicator">
            <IconButton size="small" onClick={() => onDuplicate(indicator)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove indicator">
            <IconButton size="small" color="error" onClick={() => onDelete(indicator.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {indicator.enabled && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Timeframes:
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
            {TIMEFRAME_OPTIONS.map(tf => (
              <Chip
                key={tf} size="small" label={tf}
                onClick={() => handleTfToggle(tf)}
                color={(indicator.timeframes || []).includes(tf) ? "primary" : "default"}
                variant={(indicator.timeframes || []).includes(tf) ? "filled" : "outlined"}
                sx={{ cursor: "pointer", fontSize: "0.7rem" }}
              />
            ))}
          </Box>

          {/* Weight slider */}
          <Box sx={{ mt: 1, px: 0.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">Weight: <strong>{((indicator.weight || 0.1) * 100).toFixed(0)}%</strong></Typography>
              <FormControlLabel
                control={<Switch size="small" checked={indicator.vetoCapable || false} onChange={(e) => onUpdate({ ...indicator, vetoCapable: e.target.checked })} />}
                label={<Tooltip title="When enabled in Veto First mode, this indicator can block all trades if it signals HOLD with high confidence." arrow><Typography variant="caption" color="text.secondary">Veto</Typography></Tooltip>}
                sx={{ mr: 0, ml: 1 }}
              />
            </Box>
            <Slider
              value={indicator.weight || 0.1}
              onChange={(e, v) => onUpdate({ ...indicator, weight: v })}
              min={0.05} max={1.0} step={0.05} size="small"
              sx={{ py: 0.5 }}
            />
          </Box>

          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}
          >
            <TuneIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">{expanded ? "Hide" : "Show"} parameters</Typography>
          </Box>

          {expanded && (
            <Box sx={{ mt: 1, pl: 1 }}>
              {Object.entries(indicator.params || {}).map(([key, value]) => (
                <Box key={key} sx={{ mb: 0.5 }}>
                  {typeof value === "boolean" ? (
                    <FormControlLabel
                      control={<Switch size="small" checked={value} onChange={(e) => handleParamChange(key, e.target.checked)} />}
                      label={<Typography variant="caption">{key.replace(/([A-Z])/g, " $1").trim()}</Typography>}
                    />
                  ) : (
                    <TextField
                      size="small" fullWidth variant="outlined"
                      label={key.replace(/([A-Z])/g, " $1").trim()}
                      value={value}
                      onChange={(e) => handleParamChange(key, isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.5 } }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default function IndicatorStackPanel({ settings, setSettings }) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const stack = settings.indicatorStack || { enabled: false, combineMode: "WEIGHTED_SCORE", minScoreToTrade: 30, allowTradeIfMissingData: false, consensusMinAgree: 2, indicators: [] };
  const indicators = stack.indicators || [];

  const updateStack = (updates) => {
    setSettings({ ...settings, indicatorStack: { ...stack, ...updates } });
  };

  const updateIndicator = (updated) => {
    const newIndicators = indicators.map(i => i.id === updated.id ? updated : i);
    updateStack({ indicators: newIndicators });
  };

  const deleteIndicator = (id) => {
    updateStack({ indicators: indicators.filter(i => i.id !== id) });
  };

  const duplicateIndicator = (indicator) => {
    const clone = { ...JSON.parse(JSON.stringify(indicator)), id: generateId() };
    updateStack({ indicators: [...indicators, clone] });
  };

  const addIndicator = (type) => {
    const newIndicator = {
      id: generateId(),
      type,
      enabled: true,
      timeframes: DEFAULT_TF[type] || ["5m"],
      params: INDICATOR_DEFAULTS[type] || {},
      weight: DEFAULT_WEIGHTS[type] || 0.10,
      vetoCapable: type === "ATR_FILTER",
      internalQuality: { volumeReliabilityEnabled: true, liquidityReliabilityEnabled: false, spreadReliabilityEnabled: false },
    };
    updateStack({ indicators: [...indicators, newIndicator] });
    setAddDialogOpen(false);
  };

  const enabledCount = indicators.filter(i => i.enabled).length;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShowChartIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">Signal Matrix (Indicator Stack)</Typography>
          {stack.enabled && (
            <Chip size="small" label={`${enabledCount} active`} color="primary" sx={{ ml: 1, height: 20, fontSize: "0.65rem" }} />
          )}
          {stack.enabled && <HealthBadge indicators={indicators} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1.5}>
          {/* Master toggle */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <FormControlLabel
                control={<Switch checked={stack.enabled} onChange={(e) => updateStack({ enabled: e.target.checked })} color="primary" />}
                label={<Tooltip title="Enable the modular indicator stack. When OFF, the strategy uses its original signal pipeline unchanged." arrow>
                  <Typography variant="body2" fontWeight="bold">Enable Indicator Stack</Typography>
                </Tooltip>}
              />
              {stack.enabled && <HealthBadge indicators={indicators} />}
            </Box>
          </Grid>

          {stack.enabled && (
            <>
              {/* Combine mode + min score */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Combine Mode</InputLabel>
                  <Select value={stack.combineMode || "WEIGHTED_SCORE"} onChange={(e) => updateStack({ combineMode: e.target.value })} label="Combine Mode">
                    <MenuItem value="WEIGHTED_SCORE">Weighted Score</MenuItem>
                    <MenuItem value="VETO_FIRST">Veto First</MenuItem>
                    <MenuItem value="CONSENSUS">Consensus</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Minimum net score (0-100) required to open a trade. Higher = more selective." arrow>
                  <Typography variant="caption">Min Score: <strong>{stack.minScoreToTrade || 30}</strong></Typography>
                </Tooltip>
                <Slider
                  value={stack.minScoreToTrade || 30}
                  onChange={(e, v) => updateStack({ minScoreToTrade: v })}
                  min={5} max={80} step={5} size="small"
                  marks={[{ value: 15, label: "15" }, { value: 30, label: "30" }, { value: 50, label: "50" }]}
                />
              </Grid>

              {stack.combineMode === "CONSENSUS" && (
                <Grid item xs={6}>
                  <TextField size="small" fullWidth type="number" label="Min Indicators Agree"
                    value={stack.consensusMinAgree || 2}
                    onChange={(e) => updateStack({ consensusMinAgree: parseInt(e.target.value) || 2 })}
                    inputProps={{ min: 1, max: 10 }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch size="small" checked={stack.allowTradeIfMissingData || false} onChange={(e) => updateStack({ allowTradeIfMissingData: e.target.checked })} />}
                  label={<Typography variant="caption">Allow trades when data is incomplete</Typography>}
                />
              </Grid>

              <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

              {/* Indicator list */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    <BarChartIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                    Indicators ({indicators.length})
                  </Typography>
                  <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => setAddDialogOpen(true)} variant="outlined">
                    Add
                  </Button>
                </Box>

                {indicators.length === 0 && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="caption">No indicators configured. Add indicators to start using the Signal Matrix.</Typography>
                  </Alert>
                )}

                {indicators.map(ind => (
                  <IndicatorCard
                    key={ind.id}
                    indicator={ind}
                    onUpdate={updateIndicator}
                    onDelete={deleteIndicator}
                    onDuplicate={duplicateIndicator}
                  />
                ))}
              </Grid>
            </>
          )}

          {!stack.enabled && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  Signal Matrix is disabled. The strategy uses its original signal pipeline. Enable to use modular indicator scoring.
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>

        {/* Add indicator dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(18, 23, 30, 0.95)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 3,
              boxShadow: '0 0 50px rgba(0,0,0,0.5)',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.4)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: '#3b82f6', borderRadius: 2, boxShadow: '0 0 12px #3b82f6' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: '#fff' }}>Add Indicator</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5, pl: 2.5 }}>
              Select a module to add to the signal matrix.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 1.5, pt: '12px !important' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {INDICATOR_TYPES.map(type => {
                const existing = indicators.filter(i => i.type === type.id).length;
                return (
                  <Box
                    key={type.id}
                    onClick={() => addIndicator(type.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, borderRadius: 1.5, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 2,
                        background: type.color, boxShadow: `0 0 12px 1px ${type.color}`, opacity: 0.7,
                        transition: 'width 0.25s ease, opacity 0.25s ease',
                      },
                      '&:hover': {
                        background: 'rgba(255,255,255,0.06)',
                        borderColor: 'rgba(255,255,255,0.2)',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 24px rgba(0,0,0,0.5), 0 0 15px ${type.color}15`,
                        '&::before': { width: 3, opacity: 1, boxShadow: `0 0 20px 2px ${type.color}` },
                        '& .ind-label': { color: type.color },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, position: 'relative', zIndex: 1 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        background: 'rgba(13,17,23,0.6)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: `0 0 14px ${type.color}30, inset 0 0 8px ${type.color}08`,
                        '& .MuiSvgIcon-root': { fontSize: 16, color: type.color, filter: `drop-shadow(0 0 6px ${type.color})` },
                      }}>
                        {type.icon}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography className="ind-label" sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em', transition: 'color 0.25s', lineHeight: 1.3 }}>
                          {type.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, mt: 0.15 }}>
                          {type.category}
                        </Typography>
                      </Box>
                    </Box>
                    {existing > 0 && (
                      <Chip size="small" label={`×${existing}`} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${type.color}20`, color: type.color, border: `1px solid ${type.color}40`, zIndex: 1 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.05)', px: 2, py: 1 }}>
            <Button onClick={() => setAddDialogOpen(false)} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
}
